import { A11yModule } from '@angular/cdk/a11y'
import { BreakpointObserver } from '@angular/cdk/layout'
import { AsyncPipe, isPlatformBrowser } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { animationFrameScheduler, fromEvent, map, Observable, ReplaySubject, switchMap, tap, throttleTime } from 'rxjs'

import { AnalyticsService } from 'app/core/analytics/analytics.service'
import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { Card } from 'app/core/cms/type-unions'
import {
  baseContainerSideGapLg,
  baseContainerSideGapMd,
  baseContainerSideGapSm,
  bpMaxWidthMd,
  bpMaxWidthSm,
  bpMinWidthLg,
  bpMinWidthMd,
} from 'app/static/style-vars.const'

import { InsertDynamicCardDirective } from '../../dynamic-components/insert-dynamic-card.directive'
import { RippleDirective } from '../ripple/ripple.directive'
import { TranslatePipe } from '../translate.pipe'

const MQ = {
  SM: `(max-width: ${bpMaxWidthSm}px)`,
  MD: `(min-width: ${bpMinWidthMd}px) and (max-width: ${bpMaxWidthMd}px)`,
  LG: `(min-width: ${bpMinWidthLg}px)`,
}

// TODO implement Container Queries when Chrome V 108 is current V
// todo make signal based
@Component({
  selector: 'pai-cards-carousel',
  templateUrl: './cards-carousel.component.html',
  styleUrls: ['./cards-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, A11yModule, RippleDirective, TranslatePipe, InsertDynamicCardDirective],
})
export class CardsCarouselComponent implements OnChanges, AfterViewInit, OnDestroy {
  // todo instead of the content, we need read content children (do we need templates?)
  @Input() items: readonly Card[]

  @ViewChild('scrollContainer')
  scrollContainerRef: ElementRef

  @ViewChildren('containerItem')
  containerItemRefs: QueryList<ElementRef>

  @ViewChildren('navDot')
  navDotRefs: QueryList<ElementRef>

  currentPaginationIndex = 0

  pages$?: Observable<number[]>

  get scrollContainerEl(): HTMLDivElement {
    return this.scrollContainerRef.nativeElement
  }

  // the horizontal gap used between the cards (breakpoint dependant)
  private hGap: number
  private intersectionObserver: IntersectionObserver
  private readonly changesSubject = new ReplaySubject<SimpleChanges>(1)
  private readonly breakpointObserver = inject(BreakpointObserver)
  private readonly cd = inject(ChangeDetectorRef)
  private readonly analytics = inject(AnalyticsService)
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID))

  ngOnChanges(changes: SimpleChanges): void {
    this.changesSubject.next(changes)
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.initScrollProgressListener()
      this.initPagesCalc()
      this.cd.detectChanges()
    }
  }

  ngOnDestroy(): void {
    this.changesSubject.complete()

    if (this.isBrowser) {
      this.containerItemRefs.forEach((e) => this.intersectionObserver.unobserve(e.nativeElement))
      this.intersectionObserver.disconnect()
    }
  }

  protected scrollToPage(index: number, userInteraction = true) {
    const left = Math.min(
      this.scrollContainerEl.offsetWidth * index - index * this.hGap,
      this.scrollContainerEl.scrollWidth - this.scrollContainerEl.offsetWidth,
    )
    this.currentPaginationIndex = index
    this.scrollContainerEl.scroll({ behavior: 'smooth', left })
    if (userInteraction) {
      this.analytics.trackEvent(AnalyticsEvent.CARDS_CAROUSEL_INTERACTION, { action: 'DotClick' })
    }
  }

  private initScrollProgressListener() {
    const options: IntersectionObserverInit = {
      root: this.scrollContainerEl,
      threshold: 0.01,
    }
    this.intersectionObserver = new IntersectionObserver(this.onIntersection, options)
    this.containerItemRefs.forEach((e) => this.intersectionObserver.observe(e.nativeElement))

    fromEvent(this.scrollContainerEl, 'scroll')
      .pipe(throttleTime(0, animationFrameScheduler))
      .subscribe(this.updateScrollProgress)

    this.cd.markForCheck()
  }

  private initPagesCalc() {
    this.pages$ = this.changesSubject.pipe(
      switchMap(() => this.breakpointObserver.observe([MQ.SM, MQ.MD, MQ.LG])),
      map((v) => {
        this.hGap = v.breakpoints[MQ.SM]
          ? baseContainerSideGapSm
          : v.breakpoints[MQ.MD]
            ? baseContainerSideGapMd
            : baseContainerSideGapLg
        const cardsPerPage = v.breakpoints[MQ.SM] ? 1 : v.breakpoints[MQ.MD] ? 2 : 3
        const pageCount = Math.ceil(this.items.length / cardsPerPage)
        return Array.from({ length: pageCount })
          .fill(true)
          .map((_, ix) => ix)
      }),
      tap(() => {
        this.scrollToPage(0, false)
        setTimeout(this.updateScrollProgress)
      }),
    )
    this.cd.markForCheck()
  }

  private onIntersection: IntersectionObserverCallback = (entries) => {
    /**
     * the class --no-shadow takes effect inside the {@link AnchorCardComponent }
     */
    entries.forEach((e) => e.target.classList.toggle('--no-shadow', !e.isIntersecting))
  }

  /**
   * use in browser only
   */
  private updateScrollProgress = () => {
    // last page might not be 100% offsetWidth
    const pages = this.scrollContainerEl.scrollWidth / this.scrollContainerEl.offsetWidth // eg. 2.333 | 2.5 | 2.666

    let progress = this.scrollContainerEl.scrollLeft / this.scrollContainerEl.offsetWidth

    if (progress > Math.floor(pages) - 1) {
      progress = Math.floor(progress) + (progress % 1) / (pages % 1)
    }

    this.navDotRefs.forEach((el, ix) => {
      const itemProgress = Math.max(0, 1 - Math.abs(ix - progress))
      ;(<HTMLElement>el.nativeElement).style.setProperty('--progress', `${itemProgress}`)
    })
  }
}
